import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    Box,
    Collapse,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { Link, useLocation } from "react-router-dom";
import { usePermissions } from "../../providers/PermissionsProvider";
import { buildProductionSublinks, navLinks } from "./data/navLinks";
import { getProductionSteps } from "../../helpers/http_production_steps_requests";
import { APP_VERSION } from "../../utils/data_variables";
import { DRAWER_PERMANENT_BP, DRAWER_RAIL, DRAWER_WIDTH } from "./widths";

const ink = "#0B0A08";
const gold = "#C8A459";
const muted = "#6E665A";
const logo = "https://inovartgold.com.br/assets/logo/logo.jpg";

function pathMatches(currentPath, path) {
    if (!path) return false;
    if (path === "/") return currentPath === "/" || currentPath === "/dashboard";
    return currentPath === path || currentPath.startsWith(`${path}/`);
}

function itemIsActive(currentPath, item) {
    if (item.path && pathMatches(currentPath, item.path)) return true;
    return Boolean(item.sublinks?.some((sub) => pathMatches(currentPath, sub.path)));
}

export default function AdminDrawer({ isVisible, handleVisibilityChange, onExpandChange }) {
    const theme = useTheme();
    const isPermanent = useMediaQuery(theme.breakpoints.up(DRAWER_PERMANENT_BP));
    const canHoverExpand = useMediaQuery("(hover: hover) and (pointer: fine)");
    const { pathname } = useLocation();
    const { verifyPermission } = usePermissions();
    const [openCollapses, setOpenCollapses] = useState({});
    const [isExpanded, setIsExpanded] = useState(false);
    const width = isPermanent ? (isExpanded ? DRAWER_WIDTH : DRAWER_RAIL) : DRAWER_WIDTH;

    const { data: stepsResponse } = useQuery({
        queryKey: ["getProductionSteps"],
        queryFn: getProductionSteps,
        staleTime: 1000 * 60,
    });
    const productionSteps = Array.isArray(stepsResponse?.data?.data) ? stepsResponse.data.data : null;
    const menuLinks = useMemo(
        () => navLinks.map((item) => (
            item.name === "production"
                ? { ...item, sublinks: buildProductionSublinks(productionSteps) }
                : item
        )),
        [productionSteps]
    );

    useEffect(() => {
        const next = {};
        menuLinks.forEach((item, index) => {
            if (item.collapse && itemIsActive(pathname, item)) {
                next[index] = true;
            }
        });
        setOpenCollapses((prev) => ({ ...prev, ...next }));
    }, [pathname, menuLinks]);

    const handleCollapseToggle = (index) => {
        setOpenCollapses((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const handleMouseEnter = () => {
        if (!isPermanent || !canHoverExpand) return;
        setIsExpanded(true);
        onExpandChange?.(true);
    };

    const handleMouseLeave = () => {
        if (!isPermanent || !canHoverExpand) return;
        setIsExpanded(false);
        onExpandChange?.(false);
    };

    return (
        <Drawer
            variant={isPermanent ? "permanent" : "temporary"}
            anchor="left"
            open={isPermanent ? true : isVisible}
            onClose={handleVisibilityChange}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                    width,
                    boxSizing: "border-box",
                    bgcolor: "#FFFFFF",
                    color: ink,
                    borderRight: "1px solid #E5E2DA",
                    overflowX: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    transition: "width .25s ease",
                },
            }}
        >
            <Box sx={styles.brand}>
                <Box component="img" src={logo} alt="Inovart Gold" sx={styles.logo} />
                {isExpanded || !isPermanent ? (
                    <Typography sx={styles.brandName}>Inovart Gold</Typography>
                ) : null}
            </Box>

            <List component="nav" disablePadding sx={styles.list}>
                {menuLinks.map((item, index) => (
                    verifyPermission(item.name, 1) ? (
                        <Box key={item.label}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    component={item.path ? Link : "div"}
                                    to={item.path}
                                    disabled={item.disabled}
                                    onClick={() => {
                                        if (item.collapse) handleCollapseToggle(index);
                                        else if (!isPermanent) handleVisibilityChange();
                                    }}
                                    sx={styles.item(itemIsActive(pathname, item), isExpanded || !isPermanent)}
                                >
                                    <ListItemIcon sx={styles.icon(itemIsActive(pathname, item), isExpanded || !isPermanent)}>
                                        <item.icon size={18} />
                                    </ListItemIcon>
                                    {(isExpanded || !isPermanent) && (
                                        <Typography noWrap sx={styles.label(itemIsActive(pathname, item))}>
                                            {item.label}
                                        </Typography>
                                    )}
                                    {item.collapse && (isExpanded || !isPermanent) && (
                                        <Box sx={{ ml: "auto", color: muted, display: "flex" }}>
                                            {openCollapses[index] ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                        </Box>
                                    )}
                                </ListItemButton>
                            </ListItem>

                            {item.collapse && (
                                <Collapse in={Boolean(openCollapses[index] && (isExpanded || !isPermanent))} timeout="auto" unmountOnExit>
                                    <List disablePadding>
                                        {item.sublinks.map((sublink) =>
                                            verifyPermission(sublink.name, sublink.permission) ? (
                                                <ListItemButton
                                                    key={sublink.path}
                                                    component={sublink.disabled ? "div" : Link}
                                                    to={sublink.disabled ? undefined : sublink.path}
                                                    disabled={Boolean(sublink.disabled)}
                                                    onClick={() => {
                                                        if (sublink.disabled) return;
                                                        if (!isPermanent) handleVisibilityChange();
                                                    }}
                                                    sx={styles.subItem(pathMatches(pathname, sublink.path))}
                                                >
                                                    <Typography noWrap sx={styles.subLabel(pathMatches(pathname, sublink.path))}>
                                                        {sublink.label}
                                                    </Typography>
                                                </ListItemButton>
                                            ) : null
                                        )}
                                    </List>
                                </Collapse>
                            )}
                        </Box>
                    ) : null
                ))}
            </List>

            <Typography sx={styles.version}>
                {(isExpanded || !isPermanent) ? `Versão ${APP_VERSION}` : APP_VERSION}
            </Typography>
        </Drawer>
    );
}

const styles = {
    brand: {
        height: 64,
        px: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        borderBottom: "1px solid #E5E2DA",
        flexShrink: 0,
    },
    logo: {
        height: 36,
        width: 36,
        objectFit: "cover",
        borderRadius: 1,
        flexShrink: 0,
    },
    brandName: {
        fontFamily: '"DM Serif Display", Georgia, serif',
        fontSize: 16,
        color: ink,
        whiteSpace: "nowrap",
    },
    list: {
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        py: 1.5,
    },
    item: (active, expanded) => ({
        mx: 1,
        mb: 0.25,
        minHeight: 44,
        borderRadius: 1,
        justifyContent: expanded ? "flex-start" : "center",
        px: expanded ? 1.5 : 1,
        bgcolor: active ? "rgba(200,164,89,0.14)" : "transparent",
        "&:hover": {
            bgcolor: active ? "rgba(200,164,89,0.2)" : "#F6F5F1",
        },
    }),
    icon: (active, expanded) => ({
        minWidth: 0,
        mr: expanded ? 1.5 : 0,
        color: active ? gold : muted,
        justifyContent: "center",
    }),
    label: (active) => ({
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        color: active ? gold : ink,
        textAlign: "left",
    }),
    subItem: (active) => ({
        mx: 1,
        mb: 0.25,
        pl: 6,
        minHeight: 44,
        borderRadius: 1,
        bgcolor: active ? "rgba(200,164,89,0.12)" : "transparent",
        "&:hover": {
            bgcolor: "#F6F5F1",
        },
    }),
    subLabel: (active) => ({
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        color: active ? gold : muted,
        textAlign: "left",
    }),
    version: {
        px: 2,
        py: 2,
        fontSize: 11,
        color: muted,
        textAlign: "center",
        borderTop: "1px solid #E5E2DA",
    },
};
